import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Activity, Users, Clock, FileText } from 'lucide-react';

export const CaregiversPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Enterprise API', href: '/enterprise-api' }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Cura for Caregivers – Triage Symptoms with Confidence</title>
                <meta name="description" content="Peace of mind for caregivers. Triage symptoms, get clear explanations, and coordinate care for your loved ones with AI-powered guidance ✓" />
                <link rel="canonical" href="https://mycura.org/caregivers" />
                <meta property="og:title" content="Cura for Caregivers – Triage Symptoms with Confidence" />
                <meta property="og:description" content="Peace of mind for caregivers. Triage symptoms, get clear explanations, and coordinate care for your loved ones with AI-powered guidance." />
                <meta property="og:url" content="https://mycura.org/caregivers" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Cura for Caregivers – Triage Symptoms with Confidence" />
                <meta name="twitter:description" content="Peace of mind for caregivers. Triage symptoms, get clear explanations, and coordinate care for your loved ones with AI-powered guidance." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cura for Caregivers",
                        "url": "https://mycura.org/caregivers",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
                            { "@type": "ListItem", "position": 2, "name": "For Caregivers", "item": "https://mycura.org/caregivers" }
                        ]
                    })}
                </script>
            </Helmet>
            <Navigation
                menuItems={menuItems}
                scrollToSection={(id) => {
                    const element = document.getElementById(id);
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                scrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <main className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Hero */}
                    <div className="text-center max-w-4xl mx-auto mb-24">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-6xl md:text-8xl font-['Instrument_Serif'] text-slate-900 mb-8 leading-[1]"
                        >
                            Peace of mind for <br />
                            <span className="italic text-[#003b82]">caregivers.</span>
                        </motion.h1>
                        <p className="text-xl text-slate-500 leading-relaxed max-w-2xl mx-auto">
                            Caring for a loved one is hard. Cura acts as your first line of defense, helping you triage symptoms and make informed decisions without the stress.
                        </p>
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
                        <div className="p-10 rounded-[2.5rem] bg-[#F6F8FC] border border-slate-100">
                            <Activity className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] mb-4">Symptom Triage</h3>
                            <p className="text-lg text-slate-600">
                                Not sure if it's an emergency? Describe the symptoms to Cura and get an instant assessment of urgency and recommended next steps.
                            </p>
                        </div>
                        <div className="p-10 rounded-[2.5rem] bg-[#F6F8FC] border border-slate-100">
                            <Clock className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] mb-4">Always On</h3>
                            <p className="text-lg text-slate-600">
                                Caregiving doesn't stop at 5 PM. Neither do we. Get answers at 3 AM without waking up a doctor or driving to the ER unnecessarily.
                            </p>
                        </div>
                        <div className="p-10 rounded-[2.5rem] bg-[#F6F8FC] border border-slate-100">
                            <FileText className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] mb-4">Clear Explanations</h3>
                            <p className="text-lg text-slate-600">
                                Medical jargon can be confusing. Cura explains conditions, medications, and instructions in simple, plain language you can trust.
                            </p>
                        </div>
                        <div className="p-10 rounded-[2.5rem] bg-[#F6F8FC] border border-slate-100">
                            <Users className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] mb-4">Family Support</h3>
                            <p className="text-lg text-slate-600">
                                Share Cura's insights with other family members to keep everyone on the same page about your loved one's health status.
                            </p>
                        </div>
                    </div>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default CaregiversPage;
